import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAccountIds } from "../../../auth/accessToken/components/withAccountIds";
import useStore from "../../../common/hooks/useExternalStore";
import createExternalStore from "../../../common/utils/createExternalStore";
import { CORE_API_HOSTNAME } from "../../../env";
import fulfill from "../../../fulfill";
import type {
    SettlementReportConfig,
    SettlementReportConfigDraft,
    SettlementReportConfigsResponse,
} from "../../../reports/settlementList/types";

type State = {
    configurationToDelete: SettlementReportConfig | undefined;
    showNewConfigurationModal: boolean;
    configurationToEdit: SettlementReportConfig | undefined;
    createError: undefined | "bad_request" | "conflict";
    editError: undefined | "bad_request" | "conflict";
};

const initialState: State = {
    configurationToDelete: undefined,
    showNewConfigurationModal: false,
    configurationToEdit: undefined,
    createError: undefined,
    editError: undefined,
};

const settlementReportsStore = createExternalStore(
    initialState,
    {
        toggleDeleteModal: (
            state,
            configurationToDelete: SettlementReportConfig | undefined,
        ) => ({
            ...state,
            configurationToDelete,
        }),
        toggleNewModal: (state, showNewConfigurationModal: boolean) => ({
            ...state,
            showNewConfigurationModal,
        }),
        toggleEditModal: (
            state,
            configurationToEdit: SettlementReportConfig | undefined,
        ) => ({
            ...state,
            configurationToEdit,
        }),
        setCreateError: (
            state,
            createError: "bad_request" | "conflict" | undefined,
        ) => ({
            ...state,
            createError,
        }),
        setEditError: (
            state,
            editError: "bad_request" | "conflict" | undefined,
        ) => ({
            ...state,
            editError,
        }),
        resetStore: () => initialState,
    },
    "settings/settlement_reports",
);

const fetchConfigurations = async (payload: {
    accountIds: string[];
}): Promise<Record<string, SettlementReportConfig[]>> => {
    const configurations = await Promise.all(
        payload.accountIds.map(async (accountId) => {
            const response = await fulfill.get({
                accountId,
                url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/settlements/reports/configuration`,
                handlers: {
                    200: (response: SettlementReportConfigsResponse) =>
                        response.items,
                },
            });
            return response;
        }),
    );
    return payload.accountIds.reduce(
        (acc, accountId, index) => ({
            ...acc,
            [accountId]: configurations[index],
        }),
        {},
    );
};

const createConfiguration = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            configuration: SettlementReportConfigDraft;
        };
    },
): Promise<SettlementReportConfig> => {
    return await fulfill.post<SettlementReportConfigDraft>({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/settlements/reports/configuration`,
        json: options.arg.configuration,
        handlers: {
            200: (response: SettlementReportConfig) => response,
            409: () => {
                throw new Error("conflict");
            },
            400: () => {
                throw new Error("bad_request");
            },
        },
    });
};

const updateConfiguration = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            configurationId: string;
            configuration: SettlementReportConfigDraft;
        };
    },
): Promise<SettlementReportConfig> => {
    return await fulfill.put<SettlementReportConfigDraft>({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/settlements/reports/configuration/${options.arg.configurationId}`,
        json: options.arg.configuration,
        handlers: {
            200: (response: SettlementReportConfig) => response,
            409: () => {
                throw new Error("conflict");
            },
            400: () => {
                throw new Error("bad_request");
            },
        },
    });
};

const deleteConfiguration = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            configurationId: string;
        };
    },
): Promise<SettlementReportConfig> => {
    return await fulfill.delete({
        accountId: options.arg.accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/settlements/reports/configuration/${options.arg.configurationId}`,
        handlers: {
            200: (response: SettlementReportConfig) => response,
        },
    });
};

export const useSettlementReports = () => {
    const { state, dispatch } = useStore(settlementReportsStore);
    const { prodAccountId, testAccountId, urlAccountId } = useAccountIds();
    const accountIds = [prodAccountId, testAccountId].filter(
        (x) => x,
    ) as string[];
    const { data, isLoading, mutate } = useSWR(
        () =>
            accountIds.length > 0
                ? {
                      accountIds,
                      key: `${accountIds.join(",")}/settlementReports`,
                  }
                : null,
        fetchConfigurations,
    );
    const createMutation = useSWRMutation(
        `${accountIds.join(",")}/settlementReports`,
        createConfiguration,
        {
            onSuccess: async (response) => {
                dispatch("setCreateError", undefined);
                dispatch("toggleNewModal", false);
                await mutate({
                    ...data,
                    [urlAccountId]: data?.[urlAccountId].concat(response) || [
                        response,
                    ],
                });
            },
            onError: (error) => {
                dispatch(
                    "setCreateError",
                    error.message as "bad_request" | "conflict",
                );
            },
        },
    );
    const editMutation = useSWRMutation(
        `${accountIds.join(",")}/settlementReports`,
        updateConfiguration,
        {
            onSuccess: async (response) => {
                dispatch("setEditError", undefined);
                dispatch("toggleEditModal", undefined);
                await mutate({
                    ...data,
                    [urlAccountId]:
                        data?.[urlAccountId].map((config) =>
                            config.id === response.id ? response : config,
                        ) || [],
                });
            },
            onError: (error) => {
                dispatch(
                    "setEditError",
                    error.message as "bad_request" | "conflict",
                );
            },
        },
    );
    const deleteMutation = useSWRMutation(
        `${accountIds.join(",")}/settlementReports`,
        deleteConfiguration,
        {
            onSuccess: async (response) => {
                dispatch("toggleDeleteModal", undefined);
                await mutate({
                    ...data,
                    [urlAccountId]:
                        data?.[urlAccountId].filter(
                            (config) => config.id !== response.id,
                        ) || [],
                });
            },
        },
    );

    return {
        configurations: data || {},
        isLoading:
            isLoading ||
            createMutation.isMutating ||
            editMutation.isMutating ||
            deleteMutation.isMutating,
        account_id: urlAccountId,
        configurationToDelete: state.configurationToDelete,
        showNewConfigurationModal: state.showNewConfigurationModal,
        configurationToEdit: state.configurationToEdit,
        createError: state.createError,
        editError: state.editError,
        openDeleteModal: (configurationToDelete: SettlementReportConfig) =>
            dispatch("toggleDeleteModal", configurationToDelete),
        closeDeleteModal: () => dispatch("toggleDeleteModal", undefined),
        openNewModal: () => dispatch("toggleNewModal", true),
        closeNewModal: () => dispatch("toggleNewModal", false),
        openEditModal: (configurationToEdit: SettlementReportConfig) =>
            dispatch("toggleEditModal", configurationToEdit),
        closeEditModal: () => dispatch("toggleEditModal", undefined),
        postNewConfiguration: (
            accountId: string,
            configuration: SettlementReportConfigDraft,
        ) => createMutation.trigger({ accountId: accountId, configuration }),
        removeConfiguration: (accountId: string, configurationId: string) =>
            deleteMutation.trigger({
                accountId: accountId,
                configurationId: configurationId,
            }),
        updateConfiguration: (
            accountId: string,
            configurationId: string,
            configuration: SettlementReportConfigDraft,
        ) =>
            editMutation.trigger({
                accountId: accountId,
                configurationId: configurationId,
                configuration,
            }),
        resetStore: () => dispatch("resetStore"),
    };
};
